import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users,
  Search,
  UserPlus,
  FileText,
  Activity,
  ChevronRight,
  ShieldAlert,
  Stethoscope,
  DollarSign,
  Calendar,
  MapPin,
  MoreHorizontal,
  ArrowUpRight
} from 'lucide-react';
import { Layout } from '@/components/Layout';
import { PatientTable } from '@/components/DataTable';
import { FilterPanel } from '@/components/Filters';
import { useHealthcareData } from '@/hooks/useHealthcareData';
import { IMAGES } from '@/assets/images';
import { PatientRecord, formatCurrency } from '@/lib/index';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription 
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { springPresets, fadeInUp } from '@/lib/motion';

const Patients: React.FC = () => {
  const {
    isLoading,
    data: filteredPatients,
    filters,
    updateFilter,
    kpis
  } = useHealthcareData();

  const [selectedPatient, setSelectedPatient] = useState<PatientRecord | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  const handleRowClick = (patient: PatientRecord) => {
    setSelectedPatient(patient);
    setIsDetailsOpen(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Critical': return 'text-destructive bg-destructive/10 border-destructive/20';
      case 'Stable': return 'text-primary bg-primary/10 border-primary/20';
      case 'Recovering': return 'text-accent bg-accent/10 border-accent/20';
      default: return 'text-muted-foreground bg-muted border-border';
    }
  };

  return (
    <Layout>
      <div className="relative min-h-screen p-6 space-y-8">
        {/* Background Decorative Element */}
        <div className="absolute top-0 right-0 w-1/2 h-full -z-10 opacity-5 pointer-events-none">
          <img 
            src={IMAGES.MEDICAL_EQUIPMENT_3} 
            alt="Medical Technology Background" 
            className="w-full h-full object-cover grayscale"
          />
        </div>

        {/* Page Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col md:flex-row md:items-center justify-between gap-4"
        >
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-foreground">Patient Management</h1>
            <p className="text-muted-foreground mt-1">
              Manage and analyze clinical records for {filteredPatients.length} active patients.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" className="gap-2">
              <FileText className="w-4 h-4" />
              Export PDF
            </Button>
            <Button className="gap-2 shadow-lg shadow-primary/20">
              <UserPlus className="w-4 h-4" />
              Add Patient
            </Button>
          </div>
        </motion.div>

        {/* Search and Filters Bar */}
        <motion.div 
          variants={fadeInUp}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 lg:grid-cols-4 gap-6"
        >
          <div className="lg:col-span-1">
            <Card className="h-full border-border/50 bg-card/50 backdrop-blur-md sticky top-24">
              <CardHeader className="pb-4">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Activity className="w-4 h-4 text-primary" />
                  Advanced Filters
                </CardTitle>
              </CardHeader>
              <CardContent>
                <FilterPanel 
                  activeFilters={filters}
                  onFiltersChange={(newFilters) => {
                    Object.entries(newFilters).forEach(([key, value]) => {
                      updateFilter(key as any, value);
                    });
                  }}
                />
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-3 space-y-6">
            {/* Quick Summary Metrics */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              {kpis.map((kpi, idx) => (
                <motion.div
                  key={kpi.title}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <Card className="border-border/40 bg-card/60 hover:bg-card/80 transition-colors cursor-default group overflow-hidden relative">
                    <div className="absolute top-0 right-0 p-2 opacity-10 group-hover:opacity-20 transition-opacity">
                      <ArrowUpRight className="w-12 h-12" style={{ color: kpi.color }} />
                    </div>
                    <CardContent className="p-4">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{kpi.title}</p>
                      <h3 className="text-2xl font-bold mt-1 tabular-nums">{kpi.value}</h3>
                      <div className="flex items-center gap-1 mt-2">
                        <Badge variant={kpi.trend === 'up' ? 'default' : 'secondary'} className="text-[10px] py-0 px-1">
                          {kpi.trend === 'up' ? '+' : ''}{kpi.change}%
                        </Badge>
                        <span className="text-[10px] text-muted-foreground truncate">vs last month</span>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>

            {/* Patient Data Table */}
            <Card className="border-border/50 bg-card/80 backdrop-blur-sm overflow-hidden">
              <CardHeader className="flex flex-row items-center justify-between border-b border-border/50 py-4">
                <div className="relative w-full max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search patient by name or ID..."
                    className="pl-10 bg-background/50"
                    value={filters.search}
                    onChange={(e) => updateFilter('search', e.target.value)}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Showing {filteredPatients.length} results</span>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <MoreHorizontal className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <div className="p-0 min-h-[500px]">
                {isLoading ? (
                  <div className="flex items-center justify-center h-[400px]">
                    <div className="flex flex-col items-center gap-4">
                      <div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
                      <p className="text-sm text-muted-foreground animate-pulse">Retrieving medical records...</p>
                    </div>
                  </div>
                ) : (
                  <PatientTable 
                    data={filteredPatients} 
                    onRowClick={handleRowClick} 
                  />
                )}
              </div>
            </Card>
          </div>
        </motion.div>

        {/* Patient Detail Drill-down */}
        <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
          <DialogContent className="max-w-3xl border-border bg-card/95 backdrop-blur-xl">
            {selectedPatient && (
              <>
                <DialogHeader className="pb-6 border-b border-border/50">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center border border-primary/20">
                        <Users className="w-8 h-8 text-primary" />
                      </div>
                      <div>
                        <DialogTitle className="text-2xl font-bold flex items-center gap-3">
                          {selectedPatient.name}
                          <Badge className={getStatusColor(selectedPatient.status)}>
                            {selectedPatient.status}
                          </Badge>
                        </DialogTitle>
                        <DialogDescription className="flex items-center gap-3 mt-1">
                          <span className="flex items-center gap-1"><ShieldAlert className="w-3 h-3 text-destructive" /> ID: {selectedPatient.id}</span>
                          <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {selectedPatient.region.toUpperCase()}</span>
                        </DialogDescription>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground uppercase font-semibold">Treatment Cost</p>
                      <p className="text-2xl font-bold text-primary">{formatCurrency(selectedPatient.charges)}</p>
                    </div>
                  </div>
                </DialogHeader>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 py-6">
                  {/* Clinical Specs */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-semibold flex items-center gap-2">
                      <Stethoscope className="w-4 h-4 text-primary" />
                      Biometric Specs
                    </h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 rounded-xl bg-muted/50 border border-border/50">
                        <p className="text-[10px] text-muted-foreground uppercase">Age</p>
                        <p className="text-lg font-bold">{selectedPatient.age}</p>
                      </div>
                      <div className="p-3 rounded-xl bg-muted/50 border border-border/50">
                        <p className="text-[10px] text-muted-foreground uppercase">BMI</p>
                        <p className="text-lg font-bold">{selectedPatient.bmi.toFixed(1)}</p>
                      </div>
                      <div className="p-3 rounded-xl bg-muted/50 border border-border/50">
                        <p className="text-[10px] text-muted-foreground uppercase">Smoker</p>
                        <p className="text-lg font-bold capitalize">{selectedPatient.smoker}</p>
                      </div>
                      <div className="p-3 rounded-xl bg-muted/50 border border-border/50">
                        <p className="text-[10px] text-muted-foreground uppercase">Children</p>
                        <p className="text-lg font-bold">{selectedPatient.children}</p>
                      </div>
                    </div>
                  </div>

                  {/* Treatment History */}
                  <div className="md:col-span-2 space-y-4">
                    <h4 className="text-sm font-semibold flex items-center gap-2">
                      <Activity className="w-4 h-4 text-accent" />
                      Treatment & Outcomes
                    </h4>
                    <div className="p-4 rounded-xl bg-card border border-border/50 shadow-sm">
                      <div className="flex justify-between items-center mb-4">
                        <div>
                          <p className="text-sm font-medium">Current Treatment</p>
                          <p className="text-xs text-muted-foreground">{selectedPatient.treatmentType} Protocol</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium">Risk Score</p>
                          <p className="text-lg font-bold text-destructive">{selectedPatient.riskScore}/100</p>
                        </div>
                      </div>
                      
                      <div className="space-y-3">
                        <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors group cursor-pointer">
                          <div className="w-8 h-8 rounded bg-primary/10 flex items-center justify-center text-primary">
                            <Calendar className="w-4 h-4" />
                          </div>
                          <div className="flex-1">
                            <p className="text-xs font-medium">Initial Diagnosis</p>
                            <p className="text-[10px] text-muted-foreground">Dec 14, 2025</p>
                          </div>
                          <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:translate-x-1 transition-transform" />
                        </div>
                        <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors group cursor-pointer">
                          <div className="w-8 h-8 rounded bg-accent/10 flex items-center justify-center text-accent">
                            <Activity className="w-4 h-4" />
                          </div>
                          <div className="flex-1">
                            <p className="text-xs font-medium">Biometric Scan Update</p>
                            <p className="text-[10px] text-muted-foreground">Jan 08, 2026</p>
                          </div>
                          <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <Button className="flex-1 bg-primary hover:bg-primary/90">Schedule Consultation</Button>
                      <Button variant="outline" className="flex-1">Full Case File</Button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Patients;
